import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { format, parse } from 'date-fns';
import { useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormSetError } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';
import { ScrollView, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import { useLocations } from '@/api/partners/locations/use-locations';
import { useCreateCustomer } from '@/api/partners/organizations/use-create-customer';
import { useOrganizationCars } from '@/api/partners/organizations/use-organization-cars';
import { usePaginatedCustomers } from '@/api/partners/organizations/use-paginated-customers';
import type {
  AdminReservationDraft,
  RenewalPeriod,
  ReservationType,
} from '@/api/partners/reservations/types';
import { useAdminReservationQuote } from '@/api/partners/reservations/use-admin-reservation-quote';
import type { AdminReservationErrorBody } from '@/api/partners/reservations/use-create-admin-reservation';
import { useCreateAdminReservation } from '@/api/partners/reservations/use-create-admin-reservation';
import { useAddOns } from '@/api/reservations/use-add-ons';
import { useSubscriptionExtras } from '@/api/subscriptions/use-subscription-extras';
import { InputPhone } from '@/components/input-phone';
import type { PhoneCountry } from '@/components/input-phone/types';
import { formatNumber, getDailyInsuranceCost } from '@/core';
import { useDebounce } from '@/core/hooks/use-debounced-value';
import { useHasOrganization } from '@/core/hooks/use-has-organization';
import { validatePhoneNumber } from '@/core/utils';
import {
  Button,
  Checkbox,
  ControlledInput,
  Input,
  showErrorMessage,
  Text,
} from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import Dropdown from '@/ui/dropdown';
import { Icon } from '@/ui/icon';
import { Person } from '@/ui/icons';
import { ArrowClockwise } from '@/ui/icons/arrow-clockwise';
import { Car as CarIcon } from '@/ui/icons/car';
import { Warning } from '@/ui/icons/warning';
import { InputMulti } from '@/ui/input-multi';

import { DatetimeField } from './datetime-field';
import { OptionCard } from './option-card';
import { SummarySidebar } from './summary-sidebar';

const newCustomerSchema = z.object({
  firstName: z.string().min(2, 'Minimum 2 characters'),
  lastName: z.string().min(2, 'Minimum 2 characters'),
  dob: z
    .string()
    .regex(
      /^\d{2}\/\d{2}\/\d{4}$/,
      'Date of birth must be in the format MM/DD/YYYY'
    ),
  email: z.string().email(),
});

type NewCustomerFormType = z.infer<typeof newCustomerSchema>;

// 422 bodies bypass the camelize interceptor, so keys are snake_case.
const FORM_FIELD_BY_ERROR_KEY: Record<string, keyof NewCustomerFormType> = {
  first_name: 'firstName',
  last_name: 'lastName',
  dob: 'dob',
  email: 'email',
};

const RENEWAL_OPTIONS = [
  { id: 'weekly', value: 'weekly', title: 'Weekly' },
  { id: 'monthly', value: 'monthly', title: 'Monthly' },
];

const EMPTY_QUOTE_DRAFT = {} as AdminReservationDraft;

const formatReservationErrors = (
  errors: AdminReservationErrorBody['errors']
) => {
  if (Array.isArray(errors)) {
    return errors.join(', ');
  }

  if (errors) {
    return Object.values(errors).join(', ');
  }

  return null;
};

const applyInlineReservationErrors = (
  errors: AdminReservationErrorBody['errors'],
  setPhoneError: (message: string | null) => void,
  setError: UseFormSetError<NewCustomerFormType>
) => {
  if (!errors || Array.isArray(errors)) {
    return false;
  }

  let handledInline = false;

  Object.entries(errors).forEach(([key, message]) => {
    if (key === 'phone') {
      setPhoneError(message);
      handledInline = true;
    } else if (FORM_FIELD_BY_ERROR_KEY[key]) {
      setError(FORM_FIELD_BY_ERROR_KEY[key], { message });
      handledInline = true;
    }
  });

  return handledInline;
};

const isInlineReservationError = (
  errors: AdminReservationErrorBody['errors']
) =>
  !!errors &&
  !Array.isArray(errors) &&
  Object.keys(errors).some(
    (key) => key === 'phone' || FORM_FIELD_BY_ERROR_KEY[key]
  );

export default function CreateReservation() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isEVFMC } = useHasOrganization();

  const [reservationType, setReservationType] =
    useState<ReservationType>('daily');
  const [startDatetime, setStartDatetime] = useState<Date | null>(null);
  const [endDatetime, setEndDatetime] = useState<Date | null>(null);
  const [openCalendar, setOpenCalendar] = useState<'start' | 'end' | null>(
    null
  );
  const [renewalPeriod, setRenewalPeriod] = useState<RenewalPeriod>('weekly');
  const [mileage, setMileage] = useState<number | null>(null);
  const [carId, setCarId] = useState<number | null>(null);
  const [pickupLocationId, setPickupLocationId] = useState<number | null>(null);
  const [returnLocationId, setReturnLocationId] = useState<number | null>(null);
  const [returnToSameLocation, setReturnToSameLocation] = useState(true);
  const [delivery, setDelivery] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [customerMode, setCustomerMode] = useState<'existing' | 'new'>(
    'existing'
  );
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<PhoneCountry>({ callingCode: '1' });
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [insuranceId, setInsuranceId] = useState<number | null>(null);
  const [manualDiscount, setManualDiscount] = useState('');
  const [notes, setNotes] = useState('');
  const [dailyRate, setDailyRate] = useState('');

  const debouncedDiscount = useDebounce(manualDiscount, 500);
  const debouncedDailyRate = useDebounce(dailyRate, 500);

  const isCustomSubscription = reservationType === 'custom_subscription';
  const isStandardSubscription = reservationType === 'subscription';
  const isSubscriptionLike = isStandardSubscription || isCustomSubscription;

  const { data: carsData } = useOrganizationCars();
  const { data: locations } = useLocations({});
  const { data: customersData } = usePaginatedCustomers({
    enabled: customerMode === 'existing',
  });

  const { data: addOns } = useAddOns({
    variables: { startTime: startDatetime! },
    enabled: reservationType === 'daily' && !!startDatetime,
  });

  const { data: subscriptionExtras } = useSubscriptionExtras({
    variables: {
      period: isCustomSubscription ? 'weekly' : renewalPeriod,
      carId: carId ?? 0,
    },
    enabled: isSubscriptionLike && !!carId,
  });

  const cars = useMemo(() => carsData ?? [], [carsData]);

  const availableCars = useMemo(() => {
    if (!pickupLocationId) {
      return [];
    }

    return cars.filter((car) => car.defaultLocation?.id === pickupLocationId);
  }, [cars, pickupLocationId]);

  const customers = useMemo(
    () => customersData?.pages.flatMap((p) => p.data) ?? [],
    [customersData]
  );

  const selectedCar = cars.find((car) => car.id === carId) ?? null;
  const selectedCustomer =
    customers.find((customer) => customer.id === customerId) ?? null;

  const mileageOptions = useMemo(
    () => subscriptionExtras?.extraMileageOptions ?? [],
    [subscriptionExtras?.extraMileageOptions]
  );

  const insuranceOptions = useMemo(() => {
    if (reservationType === 'daily') {
      return addOns?.insuranceOptions ?? [];
    }

    return subscriptionExtras?.insuranceOptions ?? [];
  }, [
    reservationType,
    addOns?.insuranceOptions,
    subscriptionExtras?.insuranceOptions,
  ]);

  const insuranceDropdownOptions = useMemo(() => {
    const rate =
      (isCustomSubscription && Number(dailyRate)) || selectedCar?.dailyRate;

    return insuranceOptions.map((option) => ({
      id: String(option.id),
      value: String(option.id),
      title: option.name,
      caption:
        option.name === 'No Protection'
          ? 'Free'
          : rate
          ? `+$${formatNumber(getDailyInsuranceCost(option, rate))}/day`
          : undefined,
    }));
  }, [insuranceOptions, selectedCar, isCustomSubscription, dailyRate]);

  const selectedInsuranceName = useMemo(
    () => insuranceOptions.find((option) => option.id === insuranceId)?.name,
    [insuranceOptions, insuranceId]
  );

  const insuranceOptionsReady =
    reservationType === 'daily' ? addOns != null : subscriptionExtras != null;

  const insuranceNameRef = useRef<string | null>(null);

  useEffect(() => {
    if (!insuranceId || insuranceOptions.length === 0) {
      return;
    }

    const selected = insuranceOptions.find(
      (option) => option.id === insuranceId
    );
    if (selected) {
      insuranceNameRef.current = selected.name;
    }
  }, [insuranceId, insuranceOptions]);

  useEffect(() => {
    if (!insuranceOptionsReady || insuranceOptions.length === 0) {
      return;
    }

    if (insuranceId != null) {
      const isValid = insuranceOptions.some(
        (option) => option.id === insuranceId
      );
      if (isValid) {
        return;
      }

      const byName = insuranceNameRef.current
        ? insuranceOptions.find(
            (option) => option.name === insuranceNameRef.current
          )
        : null;

      setInsuranceId(byName?.id ?? insuranceOptions[0].id);
      return;
    }

    setInsuranceId(insuranceOptions[0].id);
  }, [insuranceId, insuranceOptions, insuranceOptionsReady]);

  const { handleSubmit, control, trigger, setValue, setError, reset } =
    useForm<NewCustomerFormType>({
      defaultValues: { firstName: '', lastName: '', dob: '', email: '' },
      resolver: zodResolver(newCustomerSchema),
    });

  const [firstName, lastName, dob, email] = useWatch({
    control,
    name: ['firstName', 'lastName', 'dob', 'email'],
    disabled: customerMode !== 'new',
  });

  const newCustomerValues = {
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    dob: dob ?? '',
    email: email ?? '',
  };

  const debouncedFirstName = useDebounce(firstName ?? '');
  const debouncedLastName = useDebounce(lastName ?? '');
  const debouncedDob = useDebounce(dob ?? '');
  const debouncedEmail = useDebounce(email ?? '');
  const debouncedPhone = useDebounce(phone);

  const debouncedNewCustomerValues = useMemo(
    () => ({
      firstName: debouncedFirstName,
      lastName: debouncedLastName,
      dob: debouncedDob,
      email: debouncedEmail,
    }),
    [debouncedFirstName, debouncedLastName, debouncedDob, debouncedEmail]
  );

  const { mutate: createReservation, isPending: isCreating } =
    useCreateAdminReservation();

  const { mutate: createCustomer, isPending: isEnsuringCustomer } =
    useCreateCustomer();
  const [ensuredNewCustomer, setEnsuredNewCustomer] = useState<{
    identityKey: string;
    id: number;
    standardEarnings: boolean;
  } | null>(null);

  const newCustomerIdentityKey = useMemo(() => {
    if (customerMode !== 'new') {
      return null;
    }

    if (!newCustomerSchema.safeParse(debouncedNewCustomerValues).success) {
      return null;
    }

    return `${debouncedEmail}|${debouncedDob}|${debouncedFirstName}|${debouncedLastName}`;
  }, [
    customerMode,
    debouncedEmail,
    debouncedDob,
    debouncedFirstName,
    debouncedLastName,
    debouncedNewCustomerValues,
  ]);

  const ensuredForCurrentIdentity =
    customerMode === 'new' &&
    ensuredNewCustomer?.identityKey === newCustomerIdentityKey
      ? ensuredNewCustomer
      : null;

  const standardEarnings = ensuredForCurrentIdentity?.standardEarnings ?? false;
  const activeCustomerId =
    customerMode === 'existing'
      ? customerId
      : ensuredForCurrentIdentity?.id ?? null;

  useEffect(() => {
    if (customerMode !== 'new' || !newCustomerIdentityKey) {
      return;
    }

    if (debouncedPhone && !validatePhoneNumber(country, debouncedPhone)) {
      return;
    }

    if (ensuredNewCustomer?.identityKey === newCustomerIdentityKey) {
      return;
    }

    let ignore = false;

    createCustomer(
      {
        firstName: debouncedFirstName,
        lastName: debouncedLastName,
        dob: format(
          parse(debouncedDob, 'MM/dd/yyyy', new Date()),
          'yyyy-MM-dd'
        ),
        email: debouncedEmail,
        phone: debouncedPhone
          ? `+${country.callingCode}${debouncedPhone}`
          : undefined,
      },
      {
        onSuccess: ({ data }) => {
          if (ignore) {
            return;
          }

          setEnsuredNewCustomer({
            identityKey: newCustomerIdentityKey,
            id: data.id,
            standardEarnings: data.standardEarnings,
          });
        },
        onError: (err) => {
          if (ignore) {
            return;
          }

          const errors = err.response?.data?.errors;

          if (errors && !Array.isArray(errors) && errors.phone) {
            setPhoneError(errors.phone);
          }
        },
      }
    );

    return () => {
      ignore = true;
    };
  }, [
    customerMode,
    newCustomerIdentityKey,
    ensuredNewCustomer,
    debouncedFirstName,
    debouncedLastName,
    debouncedDob,
    debouncedEmail,
    debouncedPhone,
    country,
    createCustomer,
  ]);

  const timezone =
    locations?.find((location) => location.id === pickupLocationId)?.timezone ??
    selectedCar?.defaultLocation?.timezone;

  const toIso = (date: Date) =>
    DateTime.fromJSDate(date)
      .setZone(timezone, { keepLocalTime: true })
      .toISO() ?? date.toISOString();

  const effectiveReturnLocationId = returnToSameLocation
    ? pickupLocationId
    : returnLocationId;

  const hasValidDates =
    !!startDatetime &&
    (reservationType === 'daily'
      ? !!endDatetime
      : isStandardSubscription
      ? !!mileage
      : true);

  const baseDraftReady =
    !!carId &&
    !!pickupLocationId &&
    !!insuranceId &&
    hasValidDates &&
    (reservationType === 'daily' ? !!effectiveReturnLocationId : true);

  const buildPricingDraft = (): AdminReservationDraft | null => {
    if (
      !baseDraftReady ||
      !startDatetime ||
      !carId ||
      !pickupLocationId ||
      !insuranceId
    ) {
      return null;
    }

    return {
      reservationType,
      carId,
      startTime: toIso(startDatetime),
      endTime:
        reservationType === 'daily' && endDatetime
          ? toIso(endDatetime)
          : undefined,
      renewalPeriod: isStandardSubscription ? renewalPeriod : undefined,
      mileage: isStandardSubscription ? mileage ?? undefined : undefined,
      pickupLocationId,
      returnLocationId:
        reservationType === 'daily'
          ? effectiveReturnLocationId ?? undefined
          : undefined,
      insuranceId,
      delivery,
      deliveryAddress: delivery ? deliveryAddress || undefined : undefined,
      manualDiscount: Number(debouncedDiscount) || undefined,
      dailyRate:
        reservationType === 'custom_subscription'
          ? Number(debouncedDailyRate) || undefined
          : undefined,
    };
  };

  const buildQuoteDraft = (): AdminReservationDraft | null => {
    const pricing = buildPricingDraft();

    if (!pricing) {
      return null;
    }

    if (!activeCustomerId) {
      return null;
    }

    return { ...pricing, customerId: activeCustomerId };
  };

  const buildDraft = (): AdminReservationDraft | null => {
    const pricing = buildPricingDraft();

    if (!pricing || !activeCustomerId) {
      return null;
    }

    return {
      ...pricing,
      customerId: activeCustomerId,
      notes: notes || undefined,
    };
  };

  const customerReady =
    customerMode === 'existing'
      ? !!activeCustomerId
      : newCustomerSchema.safeParse(newCustomerValues).success &&
        !!activeCustomerId;

  const missingPricingRequirements = useMemo(() => {
    const missing: string[] = [];

    if (!startDatetime) {
      missing.push('start date');
    }

    if (reservationType === 'daily' && !endDatetime) {
      missing.push('return date');
    }

    if (!pickupLocationId) {
      missing.push('pickup location');
    }

    if (reservationType === 'daily' && !effectiveReturnLocationId) {
      missing.push('return location');
    }

    if (!carId) {
      missing.push('car');
    }

    if (!insuranceId) {
      missing.push('insurance');
    }

    if (isStandardSubscription && !mileage) {
      missing.push('mileage');
    }

    if (isCustomSubscription && isEVFMC && !dailyRate) {
      missing.push('price');
    }

    return missing;
  }, [
    carId,
    startDatetime,
    endDatetime,
    reservationType,
    pickupLocationId,
    effectiveReturnLocationId,
    insuranceId,
    isStandardSubscription,
    mileage,
    isCustomSubscription,
    isEVFMC,
    dailyRate,
  ]);

  // Host earnings quote uses customer_id only after POST …/customers succeeds.
  // Until then, pricingDraft quotes without a customer (no parallel inline create).
  const pricingDraft = useMemo(
    () => buildPricingDraft(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      reservationType,
      carId,
      startDatetime,
      endDatetime,
      renewalPeriod,
      mileage,
      pickupLocationId,
      effectiveReturnLocationId,
      insuranceId,
      delivery,
      deliveryAddress,
      debouncedDiscount,
      debouncedDailyRate,
      timezone,
    ]
  );

  const quoteDraft = useMemo(
    () => buildQuoteDraft(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      reservationType,
      carId,
      startDatetime,
      endDatetime,
      renewalPeriod,
      mileage,
      pickupLocationId,
      effectiveReturnLocationId,
      insuranceId,
      delivery,
      deliveryAddress,
      debouncedDiscount,
      debouncedDailyRate,
      timezone,
      customerMode,
      activeCustomerId,
      debouncedFirstName,
      debouncedLastName,
      debouncedDob,
      debouncedEmail,
      debouncedPhone,
      country,
    ]
  );

  // Customer is required for host earnings; pricing preview does not need one.
  const activeQuoteDraft = quoteDraft ?? pricingDraft;

  const pricingCaption = useMemo(() => {
    if (reservationType === 'daily') {
      return null;
    }

    const periodLabel =
      reservationType === 'custom_subscription'
        ? 'Weekly'
        : renewalPeriod.charAt(0).toUpperCase() + renewalPeriod.slice(1);

    const mileageLabel = isCustomSubscription
      ? 'Unlimited miles'
      : mileageOptions.find((option) => option.amount === mileage)?.name;

    return [periodLabel, mileageLabel].filter(Boolean).join(' · ') || null;
  }, [
    reservationType,
    renewalPeriod,
    mileage,
    isCustomSubscription,
    mileageOptions,
  ]);

  const {
    data: quote,
    isFetching,
    isLoading,
    isError,
    error: quoteRequestError,
  } = useAdminReservationQuote({
    variables: activeQuoteDraft ?? EMPTY_QUOTE_DRAFT,
    enabled: !!activeQuoteDraft,
  });

  const isQuoting = isLoading || (isFetching && !quote);

  const quoteResponseErrors = isError
    ? (quoteRequestError?.response?.data as AdminReservationErrorBody)?.errors
    : undefined;

  useEffect(() => {
    if (quote) {
      setPhoneError(null);
      return;
    }

    if (quoteResponseErrors) {
      applyInlineReservationErrors(
        quoteResponseErrors,
        setPhoneError,
        setError
      );
    }
  }, [quote, quoteResponseErrors, setError]);

  const quoteSidebarError =
    isError &&
    quoteResponseErrors &&
    !isInlineReservationError(quoteResponseErrors)
      ? formatReservationErrors(quoteResponseErrors) ??
        'Unable to load price breakdown'
      : null;

  const quoteLoadFailed =
    !!activeQuoteDraft && isError && !quote && !quoteSidebarError;

  const canSubmit =
    baseDraftReady && customerReady && !isCreating && !isEnsuringCustomer;

  const submitDraft = (draft: AdminReservationDraft) => {
    createReservation(draft, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['internal/partners/reservations'],
        });

        Toast.show({
          type: 'success',
          text1: 'Reservation created successfully.',
          visibilityTime: 4000,
        });

        router.replace('/partner/reservations');
      },
      onError: (err) => {
        const errors = err.response?.data?.errors;

        if (
          errors &&
          !Array.isArray(errors) &&
          applyInlineReservationErrors(errors, setPhoneError, setError)
        ) {
          return;
        }

        showErrorMessage(
          formatReservationErrors(errors) ?? 'Failed to create reservation'
        );
      },
    });
  };

  const handleCreate = () => {
    setPhoneError(null);

    if (customerMode === 'existing') {
      const draft = buildDraft();

      if (draft) {
        submitDraft(draft);
      }

      return;
    }

    if (phone && !validatePhoneNumber(country, phone)) {
      setPhoneError('Please enter a valid phone number.');
      return;
    }

    if (!activeCustomerId) {
      showErrorMessage(
        'Customer profile is still being created. Please wait a moment.'
      );
      return;
    }

    handleSubmit(() => {
      const draft = buildDraft();

      if (draft) {
        submitDraft(draft);
      }
    })();
  };

  const pickupLocation =
    locations?.find((location) => location.id === pickupLocationId) ?? null;
  const returnLocation =
    locations?.find((location) => location.id === effectiveReturnLocationId) ??
    null;

  const glance = {
    customerName:
      customerMode === 'existing'
        ? selectedCustomer
          ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
          : null
        : newCustomerValues.firstName
        ? `${newCustomerValues.firstName} ${newCustomerValues.lastName}`.trim()
        : null,
    customerPhone:
      customerMode === 'existing'
        ? selectedCustomer?.phone ?? null
        : phone
        ? `+${country.callingCode}${phone}`
        : null,
    pickupDatetime: startDatetime,
    pickupAddress:
      delivery && deliveryAddress
        ? deliveryAddress
        : pickupLocation?.address ?? null,
    returnDatetime: reservationType === 'daily' ? endDatetime : null,
    returnAddress:
      reservationType === 'daily'
        ? delivery && deliveryAddress
          ? deliveryAddress
          : returnLocation?.address ?? null
        : null,
  };

  const locationOptions =
    locations?.map((location) => ({
      id: String(location.id),
      value: String(location.id),
      title: location.address,
      caption: `${location.city}, ${location.state}`,
    })) ?? [];

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
      <View style={styles.columns}>
        <View
          style={[
            styles.formColumn,
            openCalendar ? styles.formColumnAboveSidebar : undefined,
          ]}
        >
          <View style={{ gap: 8 }}>
            <Text type="title1">Create Reservation</Text>

            <Text type="bodyRegular" style={styles.secondaryText}>
              Manually create a new reservation for a customer
            </Text>
          </View>

          {/* Dates & Location */}
          <View style={[styles.section, { zIndex: openCalendar ? 10 : 1 }]}>
            <Text type="title2">Dates & Location</Text>

            <View style={{ gap: 8 }}>
              <Text type="caption1" style={styles.secondaryText}>
                Reservation type
              </Text>

              <View style={styles.cardRow}>
                <OptionCard
                  title="Daily rental"
                  subtitle="Fixed term with start and end dates"
                  icon={
                    <Icon
                      type="naked"
                      icon={<CarIcon />}
                      fillColor={theme.colors.icon.color}
                      width={18}
                      height={18}
                    />
                  }
                  active={reservationType === 'daily'}
                  onPress={() => setReservationType('daily')}
                />

                <OptionCard
                  title="Subscription"
                  subtitle="Recurring rental with renewal period"
                  icon={
                    <Icon
                      type="naked"
                      icon={<ArrowClockwise />}
                      fillColor={theme.colors.icon.color}
                      width={18}
                      height={18}
                    />
                  }
                  active={isStandardSubscription}
                  onPress={() => {
                    setReservationType('subscription');
                    setReturnToSameLocation(true);
                  }}
                />

                {isEVFMC ? (
                  <OptionCard
                    title="Unlimited miles"
                    subtitle="Weekly subscription with unlimited mileage"
                    icon={
                      <Icon
                        type="naked"
                        icon={<ArrowClockwise />}
                        fillColor={theme.colors.icon.color}
                        width={18}
                        height={18}
                      />
                    }
                    active={isCustomSubscription}
                    onPress={() => {
                      setReservationType('custom_subscription');
                      setReturnToSameLocation(true);
                    }}
                  />
                ) : null}
              </View>
            </View>

            {isCustomSubscription && isEVFMC ? (
              <View style={styles.cardRow}>
                <Input
                  label="Billing period"
                  value="Daily"
                  editable={false}
                  disabled
                  containerStyle={{ flex: 1 }}
                />
                <Input
                  label="Renewal period"
                  value="Weekly"
                  editable={false}
                  disabled
                  containerStyle={{ flex: 1 }}
                />
                <Input
                  label="Daily Price *"
                  placeholder="$ 50.00"
                  keyboardType="numeric"
                  value={dailyRate}
                  onChangeText={(text: string) =>
                    setDailyRate(text.replace(/[^0-9.]/g, ''))
                  }
                  containerStyle={{ flex: 1 }}
                />
              </View>
            ) : null}

            {reservationType === 'daily' ? (
              <View style={[styles.cardRow, { zIndex: openCalendar ? 10 : 1 }]}>
                <View style={{ flex: 1 }}>
                  <DatetimeField
                    id="create-reservation-start"
                    label="Pickup date & time *"
                    value={startDatetime}
                    minDate={new Date()}
                    isOpen={openCalendar === 'start'}
                    onToggle={() =>
                      setOpenCalendar(openCalendar === 'start' ? null : 'start')
                    }
                    onClose={() => setOpenCalendar(null)}
                    onChange={setStartDatetime}
                  />
                </View>

                <View style={{ flex: 1 }}>
                  <DatetimeField
                    id="create-reservation-end"
                    label="Return date & time *"
                    value={endDatetime}
                    minDate={startDatetime ?? new Date()}
                    isOpen={openCalendar === 'end'}
                    onToggle={() =>
                      setOpenCalendar(openCalendar === 'end' ? null : 'end')
                    }
                    onClose={() => setOpenCalendar(null)}
                    onChange={setEndDatetime}
                  />
                </View>
              </View>
            ) : (
              <View style={{ zIndex: openCalendar ? 10 : 1 }}>
                <DatetimeField
                  id="create-reservation-start"
                  label="Start date & time *"
                  value={startDatetime}
                  minDate={new Date()}
                  isOpen={openCalendar === 'start'}
                  onToggle={() =>
                    setOpenCalendar(openCalendar === 'start' ? null : 'start')
                  }
                  onClose={() => setOpenCalendar(null)}
                  onChange={setStartDatetime}
                />
              </View>
            )}

            <Dropdown
              label="Pickup Location *"
              options={locationOptions}
              placeHolderText="Select a location"
              values={pickupLocationId ? [String(pickupLocationId)] : []}
              onSelect={(selected) => {
                const id = Number(selected[0]?.value) || null;

                if (id !== pickupLocationId) {
                  setCarId(null);
                  setMileage(null);
                  setInsuranceId(null);
                }

                setPickupLocationId(id);
              }}
            />

            {reservationType === 'daily' ? (
              <>
                <View style={styles.checkboxRow}>
                  <Checkbox
                    active={returnToSameLocation}
                    onPress={() =>
                      setReturnToSameLocation(!returnToSameLocation)
                    }
                  />

                  <Text type="bodyRegular">Return to the same location</Text>
                </View>

                {!returnToSameLocation ? (
                  <Dropdown
                    label="Return Location"
                    options={locationOptions}
                    placeHolderText="Select a location"
                    values={returnLocationId ? [String(returnLocationId)] : []}
                    onSelect={(selected) =>
                      setReturnLocationId(Number(selected[0]?.value) || null)
                    }
                  />
                ) : null}
              </>
            ) : null}

            <View style={styles.checkboxRow}>
              <Checkbox
                active={delivery}
                onPress={() => setDelivery(!delivery)}
              />

              <Text type="bodyRegular">Delivery</Text>
            </View>

            {delivery ? (
              <Input
                label="Delivery address"
                placeholder="Enter the delivery address"
                value={deliveryAddress}
                onChangeText={setDeliveryAddress}
              />
            ) : null}

            <Dropdown
              label="Car *"
              options={availableCars.map((car) => ({
                id: String(car.id),
                value: String(car.id),
                title: `${car.vehicleYear} ${car.brandName} ${car.modelName} (${car.licensePlate})`,
                caption: `$${car.dailyRate}/day`,
              }))}
              placeHolderText={
                pickupLocationId ? 'Select a car' : 'Select a location first'
              }
              values={carId ? [String(carId)] : []}
              onSelect={(selected) => {
                const id = Number(selected[0]?.value) || null;
                setCarId(id);
                setMileage(null);
                setInsuranceId(null);
              }}
            />

            {isStandardSubscription ? (
              <View style={styles.cardRow}>
                <Dropdown
                  label="Renewal period *"
                  options={RENEWAL_OPTIONS}
                  placeHolderText="Select a period"
                  values={[renewalPeriod]}
                  onSelect={(selected) => {
                    setRenewalPeriod(selected[0]?.value as RenewalPeriod);
                    setMileage(null);
                  }}
                  style={{ flex: 1 }}
                />

                <Dropdown
                  // Remount when the renewal period (or car) changes so the
                  // dropdown's displayed value resets along with the cleared
                  // mileage state, since the available tiers differ per period.
                  key={`mileage-${renewalPeriod}-${carId}`}
                  label="Mileage *"
                  options={mileageOptions.map((option) => ({
                    id: String(option.amount),
                    value: String(option.amount),
                    title: option.name,
                  }))}
                  placeHolderText={
                    carId ? 'Select mileage' : 'Select a car first'
                  }
                  values={mileage ? [String(mileage)] : []}
                  onSelect={(selected) =>
                    setMileage(Number(selected[0]?.value) || null)
                  }
                  style={{ flex: 1 }}
                />
              </View>
            ) : null}
          </View>

          {/* Customer */}
          <View style={styles.section}>
            <Text type="title2">Customer</Text>

            <View style={styles.cardRow}>
              <OptionCard
                title="Existing customer"
                subtitle="Already in your system"
                icon={
                  <Icon
                    type="naked"
                    icon={<Person />}
                    fillColor={theme.colors.icon.color}
                    width={16}
                    height={16}
                  />
                }
                active={customerMode === 'existing'}
                onPress={() => {
                  setCustomerMode('existing');
                  reset({ firstName: '', lastName: '', dob: '', email: '' });
                  setPhone('');
                  setPhoneError(null);
                }}
              />

              <OptionCard
                title="New customer"
                subtitle="Create a new profile"
                icon={
                  <Icon
                    type="naked"
                    icon={<Person />}
                    fillColor={theme.colors.icon.color}
                    width={16}
                    height={16}
                  />
                }
                active={customerMode === 'new'}
                onPress={() => {
                  setCustomerMode('new');
                  setCustomerId(null);
                }}
              />
            </View>

            {customerMode === 'existing' ? (
              <Dropdown
                label=""
                options={customers.map((customer) => ({
                  id: String(customer.id),
                  value: String(customer.id),
                  title: `${customer.firstName} ${customer.lastName}`,
                  caption: customer.email,
                }))}
                placeHolderText="Select a customer"
                values={customerId ? [String(customerId)] : []}
                onSelect={(selected) =>
                  setCustomerId(Number(selected[0]?.value) || null)
                }
              />
            ) : (
              <View style={{ gap: 16 }}>
                <View style={styles.cardRow}>
                  <View style={{ flex: 1 }}>
                    <ControlledInput
                      control={control}
                      name="firstName"
                      label="First name *"
                      placeholder="Enter first name"
                      onBlur={() => trigger('firstName')}
                      onChangeText={(text: string) => {
                        setValue('firstName', text);
                        trigger('firstName');
                      }}
                    />
                  </View>

                  <View style={{ flex: 1 }}>
                    <ControlledInput
                      control={control}
                      name="lastName"
                      label="Last name *"
                      placeholder="Enter last name"
                      onBlur={() => trigger('lastName')}
                      onChangeText={(text: string) => {
                        setValue('lastName', text);
                        trigger('lastName');
                      }}
                    />
                  </View>
                </View>

                <ControlledInput
                  control={control}
                  name="dob"
                  label="Date of birth - mm/dd/yyyy *"
                  placeholder="10/28/1996"
                  onBlur={() => trigger('dob')}
                  onChangeText={(text: string) => {
                    setValue('dob', text);
                    trigger('dob');
                  }}
                />

                <ControlledInput
                  control={control}
                  name="email"
                  label="Email *"
                  placeholder="Enter email address"
                  autoComplete="email"
                  spellCheck={false}
                  onBlur={() => trigger('email')}
                  onChangeText={(text: string) => {
                    setValue('email', text);
                    trigger('email');
                  }}
                />

                <View style={{ gap: 8 }}>
                  <InputPhone
                    onChangeText={(text: string) => {
                      setPhone(text);
                      setPhoneError(null);
                    }}
                    onCountryChange={(c: PhoneCountry) => setCountry(c)}
                  />

                  {phoneError ? (
                    <Text type="caption1" style={styles.errorText}>
                      {phoneError}
                    </Text>
                  ) : null}
                </View>

                {standardEarnings ? (
                  <Alert
                    type={AlertType.BRAND}
                    icon={<Warning />}
                    body={`This customer's first rental with your company was through Eon. You'll receive the standard earnings rate${
                      selectedCar
                        ? ` of ${Math.round(
                            (1 - selectedCar.managementCommissionPercentage) *
                              100
                          )}%`
                        : ''
                    } for this reservation.`}
                  />
                ) : null}
              </View>
            )}
          </View>

          {/* Insurance & Other */}
          <View style={styles.section}>
            <Text type="title2">Insurance & Other</Text>

            <Dropdown
              label="Insurance *"
              options={insuranceDropdownOptions}
              placeHolderText="Select insurance"
              values={insuranceId ? [String(insuranceId)] : []}
              onSelect={(selected) =>
                setInsuranceId(Number(selected[0]?.value) || null)
              }
            />

            <Input
              label="Manual discount"
              placeholder="$ 0"
              value={manualDiscount}
              keyboardType="numeric"
              onChangeText={(text: string) =>
                setManualDiscount(text.replace(/[^0-9.]/g, ''))
              }
            />

            <InputMulti
              label="Notes"
              placeholder="Add any notes about this reservation"
              text={notes}
              onChangeText={setNotes}
            />
          </View>

          <View style={styles.footer}>
            <Button
              text="Cancel"
              type="ghost"
              onPress={() => router.back()}
              style={{ width: 'auto' }}
            />

            <Button
              text="Create Reservation"
              type="primary"
              loading={isCreating}
              disabled={!canSubmit}
              onPress={handleCreate}
              style={{ width: 'auto', minWidth: 180 }}
            />
          </View>
        </View>

        <View style={styles.sidebarColumn}>
          <SummarySidebar
            glance={glance}
            quote={activeQuoteDraft ? quote : undefined}
            isQuoting={
              !!activeQuoteDraft &&
              (isQuoting || (!quote && !isError && !quoteSidebarError))
            }
            quoteError={
              quoteSidebarError ??
              (quoteLoadFailed ? 'Unable to load price breakdown' : null)
            }
            missingRequirements={missingPricingRequirements}
            insuranceName={selectedInsuranceName}
            reservationType={reservationType}
            pricingCaption={pricingCaption}
          />
        </View>
      </View>
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  page: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  pageContent: {
    paddingTop: { xs: 24, md: 56 },
    paddingHorizontal: { xs: 16, md: 56 },
    paddingBottom: { xs: 16, md: 56 },
  },
  columns: {
    flexDirection: { xs: 'column', md: 'row' },
    gap: 32,
    alignItems: 'flex-start',
  },
  formColumn: {
    flex: 1,
    gap: 32,
    maxWidth: 720,
  },
  formColumnAboveSidebar: {
    position: 'relative',
    zIndex: 20,
  },
  sidebarColumn: {
    width: { xs: '100%', md: 450 },
    zIndex: 1,
  },
  section: {
    gap: 16,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
  },
  cardRow: {
    flexDirection: 'row',
    gap: 16,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
  },
  secondaryText: {
    color: theme.colors.text.secondary,
  },
  errorText: {
    color: theme.colors.input.error.helperText,
  },
}));
